import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/store/hooks';
import { importFromCSV } from '@/store/slices/workAuthSlice';
import { workAuthService } from '@/services/workAuthService';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Upload, Download, FileSpreadsheet, CheckCircle, AlertTriangle, Info, XCircle } from 'lucide-react';
import { parse } from 'csv-parse/browser/esm/sync';

interface CSVRow {
  [key: string]: string;
}

interface ValidationError {
  row: number;
  field: string;
  value: string;
}

interface DuplicateEmployee {
  employeeId: string;
  employeeName: string;
  rows: number[];
}

interface CSVPreview {
  validRows: CSVRow[];
  invalidRows: Array<{ row: number; data: CSVRow; errors: ValidationError[] }>;
  duplicates: DuplicateEmployee[];
  columnErrors: string[];
  totalRows: number;
}

const WorkAuthImportPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState<boolean>(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [csvPreview, setCSVPreview] = useState<CSVPreview | null>(null);
  const [validating, setValidating] = useState<boolean>(false);
  const [importJobs, setImportJobs] = useState<any[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  // Load import jobs on mount
  React.useEffect(() => {
    loadImportJobs();
  }, []);

  const loadImportJobs = async () => {
    setLoadingJobs(true);
    try {
      const jobs = await workAuthService.getImportJobs();
      setImportJobs(jobs || []);
    } catch (err) {
      console.error('❌ Failed to load import jobs:', err);
      setImportJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const validateCSV = async (file: File) => {
    setValidating(true);
    setError(null);
    setCSVPreview(null);

    try {
      const text = await file.text();
      const records = parse(text, {
        columns: true,
        skip_empty_lines: true,
        trim: true
      }) as CSVRow[];

      if (records.length === 0) {
        setError('CSV file is empty');
        setValidating(false);
        return;
      }

      // Required columns
      const requiredColumns = [
        'Employee ID (Dayforce)',
        'Employee Name',
        'Document Type',
        'Document Expiration Date / Section 1 Work Authoriz'
      ];

      const actualColumns = Object.keys(records[0]);
      const missingColumns = requiredColumns.filter(col => !actualColumns.includes(col));

      if (missingColumns.length > 0) {
        setError(`Missing required columns: ${missingColumns.join(', ')}`);
        setValidating(false);
        return;
      }

      // Validate each row and check for duplicates
      const validRows: CSVRow[] = [];
      const invalidRows: Array<{ row: number; data: CSVRow; errors: ValidationError[] }> = [];
      const employeeMap = new Map<string, { name: string; rows: number[] }>();

      records.forEach((record, index) => {
        const rowNumber = index + 2; // +2 because index 0 is row 2 (after header)
        const errors: ValidationError[] = [];

        const employeeId = record['Employee ID (Dayforce)']?.trim();
        const formCode = record['Form Code']?.trim();

        // Either Employee ID or Form Code must be present
        if (!employeeId && !formCode) {
          errors.push({
            row: rowNumber,
            field: 'Employee ID (Dayforce) or Form Code',
            value: 'Both fields are empty - at least one is required'
          });
        }

        // Other required fields
        if (!record['Employee Name']?.trim()) {
          errors.push({ row: rowNumber, field: 'Employee Name', value: record['Employee Name'] || '' });
        }
        if (!record['Document Expiration Date / Section 1 Work Authoriz']?.trim()) {
          errors.push({ row: rowNumber, field: 'Document Expiration Date / Section 1 Work Authoriz', value: record['Document Expiration Date / Section 1 Work Authoriz'] || '' });
        }

        if (errors.length > 0) {
          invalidRows.push({ row: rowNumber, data: record, errors });
        } else {
          validRows.push(record);
        }

        // Track by Employee ID OR Form Code to detect duplicates
        // Use Employee ID as primary identifier, fallback to Form Code
        const identifier = employeeId || formCode;
        if (identifier) {
          if (!employeeMap.has(identifier)) {
            employeeMap.set(identifier, {
              name: record['Employee Name']?.trim() || '',
              rows: [rowNumber]
            });
          } else {
            employeeMap.get(identifier)!.rows.push(rowNumber);
          }
        }
      });

      // Find duplicates (employees appearing more than once)
      const duplicates: DuplicateEmployee[] = [];
      employeeMap.forEach((data, employeeId) => {
        if (data.rows.length > 1) {
          duplicates.push({
            employeeId,
            employeeName: data.name,
            rows: data.rows
          });
        }
      });

      setCSVPreview({
        validRows,
        invalidRows,
        duplicates,
        columnErrors: [],
        totalRows: records.length
      });
    } catch (err: any) {
      setError(`Failed to parse CSV: ${err.message}`);
    } finally {
      setValidating(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validate file type
      if (!file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }

      setSelectedFile(file);
      setError(null);
      setImportResult(null);

      // Validate CSV immediately
      await validateCSV(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !csvPreview) {
      setError('Please select a file to import');
      return;
    }

    if (csvPreview.validRows.length === 0) {
      setError('No valid rows to import');
      return;
    }

    if (csvPreview.duplicates.length > 0) {
      setError('Cannot import: CSV contains duplicate employees. Please remove duplicates and try again.');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      // Create a new CSV file with only valid rows
      const { stringify } = await import('csv-stringify/browser/esm/sync');

      // Get original column headers
      const headers = Object.keys(csvPreview.validRows[0]);

      // Convert valid rows back to CSV
      const csvContent = stringify(csvPreview.validRows, {
        header: true,
        columns: headers
      });

      // Create a new File object with only valid rows
      const validOnlyFile = new File([csvContent], selectedFile.name, {
        type: 'text/csv'
      });

      const result = await dispatch(importFromCSV(validOnlyFile)).unwrap();

      // Refresh jobs list immediately after queuing
      loadImportJobs();

      // Poll for job status
      const jobId = result.jobId;
      let pollCount = 0;
      const pollInterval = setInterval(async () => {
        try {
          const jobStatus = await workAuthService.getImportJobStatus(jobId);
          pollCount++;

          // Update progress
          console.log(`Import progress: ${jobStatus.progress}%`, jobStatus);

          // Only refresh jobs list every 3rd poll (every 15 seconds) to reduce API calls
          if (pollCount % 3 === 0) {
            loadImportJobs();
          }

          if (jobStatus.status === 'COMPLETED') {
            clearInterval(pollInterval);
            setImportResult(jobStatus.result);
            setSelectedFile(null);
            setCSVPreview(null);
            setImporting(false);
            loadImportJobs(); // Final refresh

            // Reset file input
            const fileInput = document.getElementById('csvFile') as HTMLInputElement;
            if (fileInput) fileInput.value = '';
          } else if (jobStatus.status === 'FAILED') {
            clearInterval(pollInterval);
            setError(jobStatus.error || 'Import failed');
            setImporting(false);
            loadImportJobs(); // Final refresh
          }
        } catch (pollError) {
          console.error('Error polling job status:', pollError);
        }
      }, 5000); // Poll every 5 seconds (reduced from 2s)

      // Clean up interval on unmount
      return () => clearInterval(pollInterval);
    } catch (err: any) {
      setError(err.message || 'Failed to import CSV file');
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    // Create CSV template with actual column names from the user's CSV format
    const template = `Account Code,Form Code,Requestor Name,Employee Name,Employee SSN,Employee Start Date,Termination Date,Citizenship Status,Document Type,Document Title,Document Expiration Date / Section 1 Work Authoriz,DOB,Employee ID (Dayforce),Country,Code,Notice Given,Comments,Work Location
ACC001,FORM-I9,manager@example.com,John Doe,1234,2024-01-15,,U.S. Citizen,I-9 Form,I-9 Employment Eligibility,2025-12-31,1990-05-12,EMP001,USA,WA101,yes,Initial hire,Store 101 Warehouse
ACC002,FORM-WP,manager@example.com,Jane Smith,5678,2024-03-20,2024-12-31,Permanent Resident,Work Permit,H-1B Work Authorization,2025-06-30,1988-08-22,EMP002,USA,SA102,no,Seasonal employee,Store 102 Sales Floor`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'work_auth_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/hr/work-auth/dashboard')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Import Work Authorizations</h1>
          <p className="text-muted-foreground mt-1">Import employee work authorization data from CSV file</p>
        </div>
      </div>


      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle>Upload CSV File</CardTitle>
          <CardDescription>Select and upload your CSV file containing work authorization data</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
            <FileSpreadsheet className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <input
              id="csvFile"
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => document.getElementById('csvFile')?.click()}
              className="cursor-pointer"
            >
              <Upload className="h-4 w-4 mr-2" />
              Select CSV File
            </Button>
            {selectedFile && (
              <div className="mt-4">
                <p className="text-sm text-foreground">Selected file:</p>
                <p className="font-medium">{selectedFile.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(selectedFile.size / 1024).toFixed(2)} KB
                </p>
              </div>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {validating && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-2"></div>
              <span className="text-sm text-muted-foreground">Validating CSV...</span>
            </div>
          )}

          <Button
            onClick={handleImport}
            disabled={!selectedFile || Boolean(importing) || Boolean(validating) || (csvPreview ? (csvPreview.validRows.length === 0 || csvPreview.duplicates.length > 0) : false)}
            className="w-full"
          >
            {importing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Importing...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Import CSV {csvPreview && `(${csvPreview.validRows.length} valid rows)`}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* CSV Preview */}
      {csvPreview && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              CSV Preview
            </CardTitle>
            <CardDescription>
              {csvPreview.totalRows} total rows • {csvPreview.validRows.length} valid • {csvPreview.invalidRows.length} invalid
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Total Rows</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{csvPreview.totalRows}</p>
              </div>
              <div className="bg-green-50 dark:bg-green-950 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Valid Rows</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{csvPreview.validRows.length}</p>
              </div>
              <div className="bg-red-50 dark:bg-red-950 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Invalid Rows</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{csvPreview.invalidRows.length}</p>
              </div>
            </div>

            {/* Duplicate Employees Warning */}
            {csvPreview.duplicates.length > 0 && (
              <Alert variant="destructive" className="bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-800">
                <AlertTriangle className="h-4 w-4 text-red-700 dark:text-red-400" />
                <AlertTitle className="text-red-900 dark:text-red-100">Duplicate Employees Detected</AlertTitle>
                <AlertDescription>
                  <p className="mb-2 text-red-900 dark:text-red-100 font-semibold">{csvPreview.duplicates.length} employee{csvPreview.duplicates.length > 1 ? 's' : ''} appear{csvPreview.duplicates.length === 1 ? 's' : ''} multiple times in the CSV. Please remove duplicates before importing.</p>
                  <div className="mt-3 space-y-2">
                    {csvPreview.duplicates.map((dup, idx) => (
                      <div key={idx} className="text-sm bg-red-600 text-white dark:bg-red-700 p-3 rounded font-medium">
                        <strong>{dup.employeeId}</strong> - {dup.employeeName} appears in rows: {dup.rows.join(', ')}
                      </div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Invalid Rows Warning */}
            {csvPreview.invalidRows.length > 0 && (
              <Alert variant="destructive" className="bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-800">
                <XCircle className="h-4 w-4 text-red-700 dark:text-red-400" />
                <AlertTitle className="text-red-900 dark:text-red-100">Incomplete Records Detected</AlertTitle>
                <AlertDescription>
                  <p className="text-red-900 dark:text-red-100 font-semibold">{csvPreview.invalidRows.length} row{csvPreview.invalidRows.length > 1 ? 's' : ''} will be ignored due to missing required fields. These rows are highlighted below.</p>
                </AlertDescription>
              </Alert>
            )}

            {/* Preview Table */}
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="max-h-96 overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-muted">
                    <TableRow>
                      <TableHead className="w-16">Row</TableHead>
                      <TableHead>Employee ID / Form Code</TableHead>
                      <TableHead>Employee Name</TableHead>
                      <TableHead>Expiration Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Invalid rows first (highlighted) */}
                    {csvPreview.invalidRows.map((item) => {
                      const identifier = item.data['Employee ID (Dayforce)'] || item.data['Form Code'];
                      return (
                        <TableRow key={`invalid-${item.row}`} className="bg-red-100 dark:bg-red-950/50 hover:bg-red-200 dark:hover:bg-red-950/70">
                          <TableCell className="font-bold text-red-700 dark:text-red-300">{item.row}</TableCell>
                          <TableCell className="text-sm font-medium">
                            {identifier || <span className="text-red-700 dark:text-red-300 font-bold italic">Missing</span>}
                          </TableCell>
                          <TableCell className="text-sm font-medium">
                            {item.data['Employee Name'] || <span className="text-red-700 dark:text-red-300 font-bold italic">Missing</span>}
                          </TableCell>
                          <TableCell className="text-sm font-medium">
                            {item.data['Document Expiration Date / Section 1 Work Authoriz'] || <span className="text-red-700 dark:text-red-300 font-bold italic">Missing</span>}
                          </TableCell>
                          <TableCell>
                            <span className="text-xs bg-red-600 text-white dark:bg-red-700 px-3 py-1 rounded font-semibold">
                              Will be ignored
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Valid rows */}
                    {csvPreview.validRows.slice(0, 10).map((row, index) => {
                      const rowNumber = csvPreview.invalidRows.length + index + 2;
                      const identifier = row['Employee ID (Dayforce)'] || row['Form Code'];
                      return (
                        <TableRow key={`valid-${index}`}>
                          <TableCell className="font-medium text-muted-foreground">{rowNumber}</TableCell>
                          <TableCell className="text-sm">{identifier}</TableCell>
                          <TableCell className="text-sm">{row['Employee Name']}</TableCell>
                          <TableCell className="text-sm">{row['Document Expiration Date / Section 1 Work Authoriz']}</TableCell>
                          <TableCell>
                            <span className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded">
                              Ready
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            {csvPreview.validRows.length > 10 && (
              <p className="text-sm text-muted-foreground text-center">
                Showing first 10 valid rows. {csvPreview.validRows.length - 10} more valid rows will be imported.
              </p>
            )}

            {csvPreview.validRows.length === 0 && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertTitle>No Valid Rows Found</AlertTitle>
                <AlertDescription>
                  All rows in the CSV have missing required fields. Please fix the errors and try again.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Import Results */}
      {importResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {importResult.success ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-yellow-600" />
              )}
              Import Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Records Processed</p>
                <p className="text-2xl font-bold text-blue-600">{importResult.employeesProcessed}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Employees Created</p>
                <p className="text-2xl font-bold text-green-600">{importResult.employeesCreated}</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Employees Updated</p>
                <p className="text-2xl font-bold text-yellow-600">{importResult.employeesUpdated}</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Work Auths Created</p>
                <p className="text-2xl font-bold text-purple-600">{importResult.workAuthsCreated}</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Work Auths Updated</p>
                <p className="text-2xl font-bold text-indigo-600">{importResult.workAuthsUpdated}</p>
              </div>
              {importResult.errors.length > 0 && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground">Errors</p>
                  <p className="text-2xl font-bold text-red-600">{importResult.errors.length}</p>
                </div>
              )}
            </div>

            {/* Validation Errors */}
            {importResult.errors && importResult.errors.length > 0 && (
              <div className="space-y-3">
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertTitle>Validation Errors Found</AlertTitle>
                  <AlertDescription>
                    The following rows have validation errors. Please fix these issues in your CSV file and try again.
                  </AlertDescription>
                </Alert>

                <div className="border border-destructive/20 rounded-lg overflow-hidden">
                  <div className="max-h-96 overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-destructive/10">
                        <TableRow>
                          <TableHead className="w-24 font-semibold">Row</TableHead>
                          <TableHead className="font-semibold">Error</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importResult.errors.map((err: any, idx: number) => (
                          <TableRow key={idx} className="hover:bg-destructive/5">
                            <TableCell className="font-medium text-destructive">
                              {err.row}
                            </TableCell>
                            <TableCell className="text-sm">
                              {err.error}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>How to Fix</AlertTitle>
                  <AlertDescription>
                    <ul className="text-sm space-y-1 mt-2 list-disc list-inside">
                      <li>Open your CSV file in a spreadsheet application</li>
                      <li>Navigate to the row numbers listed above</li>
                      <li>Ensure required fields are filled: <strong>Employee ID (Dayforce)</strong>, <strong>Employee Name</strong>, and <strong>Document Expiration Date / Section 1 Work Authoriz</strong></li>
                      <li>Save the file and try importing again</li>
                    </ul>
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {importResult.success && (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertTitle>Import Successful!</AlertTitle>
                <AlertDescription>
                  All records have been imported successfully. You can now view the work authorizations in the dashboard.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2">
              <Button onClick={() => navigate('/hr/work-auth/dashboard')}>
                Go to Dashboard
              </Button>
              <Button variant="outline" onClick={() => setImportResult(null)}>
                Import Another File
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import Jobs History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Import History
          </CardTitle>
          <CardDescription>
            View your recent CSV import jobs and their status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingJobs ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading import history...
            </div>
          ) : !importJobs || importJobs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No import jobs yet. Upload a CSV file to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Results</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Completed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {importJobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="font-medium">
                      {job.metadata?.fileName || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {job.status === 'COMPLETED' && (
                        <span className="inline-flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          Completed
                        </span>
                      )}
                      {job.status === 'PROCESSING' && (
                        <span className="inline-flex items-center gap-1 text-blue-600">
                          <Info className="h-4 w-4" />
                          Processing
                        </span>
                      )}
                      {job.status === 'QUEUED' && (
                        <span className="inline-flex items-center gap-1 text-gray-600">
                          <Info className="h-4 w-4" />
                          Queued
                        </span>
                      )}
                      {job.status === 'FAILED' && (
                        <span className="inline-flex items-center gap-1 text-red-600">
                          <XCircle className="h-4 w-4" />
                          Failed
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            job.status === 'COMPLETED' ? 'bg-green-600' :
                            job.status === 'FAILED' ? 'bg-red-600' :
                            'bg-blue-600'
                          }`}
                          style={{ width: `${job.progress}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {job.progress}%
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {job.result ? (
                        <div className="space-y-1">
                          <div>Created: {job.result.employeesCreated || 0}</div>
                          <div>Updated: {job.result.employeesUpdated || 0}</div>
                          {job.result.errors?.length > 0 && (
                            <div className="text-red-600">
                              Errors: {job.result.errors.length}
                            </div>
                          )}
                        </div>
                      ) : job.error ? (
                        <div className="text-red-600 text-xs">{job.error}</div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {job.startedAt ? new Date(job.startedAt).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {job.completedAt ? new Date(job.completedAt).toLocaleString() : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkAuthImportPage;